import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { City } from '../../../types/city.types';

interface CitiesTableProps {
  cities: City[];
  onReorder: (reorderedCities: City[]) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
}

const renderDragHandle = (attributes: any, listeners: any) => (
  <Box
    {...attributes}
    {...listeners}
    sx={{
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'grab',
      '&:active': {
        cursor: 'grabbing',
      },
    }}
  >
    <DragIndicatorIcon sx={{ width: '24px', height: '24px', color: 'var(--palette-light-text-secondary-value-hex)' }} />
  </Box>
);

const renderLockedDefault = () => (
  <Box
    sx={{
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <PushPinIcon sx={{ width: '20px', height: '20px', color: 'var(--palette-light-brand-primary-value-hex)' }} />
  </Box>
);

const renderActions = (onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void, id: string) => (
  <Box sx={{ width: '56px', display: 'flex', justifyContent: 'flex-end' }}>
    <IconButton
      onClick={(e) => onMenuOpen(e, id)}
      sx={{
        width: '56px',
        height: '56px',
        borderRadius: 'var(--palette-light-4-value)',
        color: 'var(--palette-light-brand-primary-value-hex)',
        '&:hover': {
          backgroundColor: 'rgba(103, 49, 79, 0.04)',
        },
      }}
    >
      <MoreHorizIcon sx={{ fontSize: '24px' }} />
    </IconButton>
  </Box>
);

const renderName = (city: City) => (
  <Typography sx={{ fontWeight: 500, color: 'var(--palette-light-text-primary-value-hex)' }}>
    {city.name}
  </Typography>
);

const renderDefaultBadge = (city: City) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Typography
      sx={{
        fontSize: 'var(--palette-light-font-size-small-value, 13px)',
        color: city.isDefault
          ? 'var(--palette-light-brand-primary-value-hex)'
          : 'var(--palette-light-text-secondary-value-hex)',
        fontWeight: city.isDefault ? 600 : 400,
      }}
    >
      {city.isDefault ? 'Default' : '—'}
    </Typography>
  </Box>
);

// Sortable row for a non-default city (the default city is pinned to the top).
const SortableCityRow: React.FC<{
  city: City;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
}> = ({ city, onMenuOpen }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: city.cityId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--palette-light-4-value)',
        padding: '11px 0',
        borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
        backgroundColor: 'var(--palette-light-background-primary-value-hex)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {renderDragHandle(attributes, listeners)}
      <Box sx={{ flex: 1 }}>{renderName(city)}</Box>
      <Box sx={{ width: '80px' }}>{renderDefaultBadge(city)}</Box>
      {renderActions(onMenuOpen, city.cityId)}
    </Box>
  );
};

// Pinned row for the default city — always first, not draggable.
const PinnedDefaultRow: React.FC<{
  city: City;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
}> = ({ city, onMenuOpen }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--palette-light-4-value)',
      padding: '11px 0',
      borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
      backgroundColor: 'var(--palette-light-background-primary-value-hex)',
    }}
  >
    {renderLockedDefault()}
    <Box sx={{ flex: 1 }}>{renderName(city)}</Box>
    <Box sx={{ width: '80px' }}>{renderDefaultBadge(city)}</Box>
    {renderActions(onMenuOpen, city.cityId)}
  </Box>
);

const CitiesTable: React.FC<CitiesTableProps> = ({
  cities,
  onReorder,
  onMenuOpen,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = cities.findIndex((city) => city.cityId === active.id);
      const newIndex = cities.findIndex((city) => city.cityId === over.id);

      const reordered = [...cities];
      const [removed] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, removed);

      onReorder(reordered);
    }
  };

  const sortableCityIds = cities
    .filter((city) => !city.isDefault)
    .map((city) => city.cityId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* Table Header */}
      <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)', padding: 'var(--palette-light-3-value) 0', borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)' }}>
        <Box sx={{ width: '24px' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            City name
          </Typography>
        </Box>
        <Box sx={{ width: '80px' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Default
          </Typography>
        </Box>
        <Box sx={{ width: '56px' }} />
      </Box>

      {/* Table Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {cities.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '32px 0' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              No cities found. Add your first city to get started.
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableCityIds}
              strategy={verticalListSortingStrategy}
            >
              {cities.map((city) =>
                city.isDefault ? (
                  <PinnedDefaultRow
                    key={city.cityId}
                    city={city}
                    onMenuOpen={onMenuOpen}
                  />
                ) : (
                  <SortableCityRow
                    key={city.cityId}
                    city={city}
                    onMenuOpen={onMenuOpen}
                  />
                )
              )}
            </SortableContext>
          </DndContext>
        )}
      </Box>
    </Box>
  );
};

export default CitiesTable;
